import { IBim360Project } from "@mctypes/forge.types";
import { IFilterItem } from "@mctypes/global.types";
import { IRootState } from "../../../store";

const isValueEmpty = (value: unknown): boolean =>
  value == null || value === "" || (Array.isArray(value) && value.length === 0);

const pad = (value: number): string => String(value).padStart(2, "0");

const getValidDate = (value: string | Date | null | undefined): Date | null => {
  if (value == null || value === "") {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const isValidDateValue = (value: unknown): boolean => {
  if (value == null || value === "") {
    return false;
  }

  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(date.getTime()) === false;
};

export const formatDateTimeCompact = (dateValue: string | Date | null | undefined): string => {
  const date = getValidDate(dateValue);
  if (date == null) {
    return "";
  }

  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())}-${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
};

export const formatDateTimeWithSeconds = (dateValue: string | Date | null | undefined): string => {
  const date = getValidDate(dateValue);
  if (date == null) {
    return "";
  }

  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())} ${pad(date.getHours())}.${pad(
    date.getMinutes(),
  )}.${pad(date.getSeconds())}`;
};

export const formatDateYmdDots = (dateValue: string | Date | null | undefined): string => {
  const date = getValidDate(dateValue);
  if (date == null) {
    return "";
  }

  return `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())}`;
};

export const formatDateYmdDotsWithTime = (dateValue: string | Date | null | undefined): string => {
  const date = getValidDate(dateValue);
  if (date == null) {
    return "";
  }

  return `${formatDateYmdDots(date)} - ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const formatDateDmyDotsWithTime = (dateValue: string | Date | null | undefined): string => {
  const date = getValidDate(dateValue);
  if (date == null) {
    return "";
  }

  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
};

export const formatDateYmdDashes = (dateValue: string | Date | null | undefined): string => {
  const date = getValidDate(dateValue);
  if (date == null) {
    return "";
  }

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const formatDateDmySlashes = (dateValue: string | Date | null | undefined): string => {
  const date = getValidDate(dateValue);
  if (date == null) {
    return "";
  }

  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
};

export const formatDate = (dateStr: string) => {
  if (isDateValidFrontend(dateStr) === false) {
    return dateStr;
  }
  const date = getValidDate(dateStr);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} - ${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
};

export const formatHtmlInputDateString = (isoString: string) => {
  if (isoString == null || isoString === "") {
    return "";
  }

  return formatDateYmdDashes(isoString);
};

export const formatDateToNorwegianDate = (isoString: string) => {
  if (isoString == null || isoString === "") {
    return "";
  }

  return formatShortDate(isoString);
};

export const parseNorwegianDate = (value: string | Date | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;

  // Strict format check: dd.MM.yyyy
  const norwegianDateRegex = /^\d{2}\.\d{2}\.\d{4}$/;
  if (!norwegianDateRegex.test(value)) return null;

  const [dayStr, monthStr, yearStr] = value.split(".");
  const day = Number(dayStr);
  const month = Number(monthStr);
  const year = Number(yearStr);

  const date = new Date(year, month - 1, day);

  // Validate that the constructed date matches input (catches invalid dates like 31.02.2026)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
};

export const formatShortDate = (dateStr: string | Date, hideOldDate: boolean = false): string => {
  if (dateStr instanceof Date) {
    dateStr = dateStr.toISOString();
  }

  if (isDateValidFrontend(dateStr) === false) {
    return dateStr;
  }

  const date = getValidDate(dateStr);

  if (hideOldDate && date < new Date("2000-01-01")) {
    return "";
  }
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

export const formatDateWithoutYear = (dateStr: string) => {
  if (isDateValidFrontend(dateStr) === false) {
    return dateStr;
  }
  return formatDateDmySlashes(dateStr);
};

export const formatInputDate = (dateStr: string) => {
  return formatDateYmdDots(dateStr);
};

export const isDateValidFrontend = (str: string): boolean => {
  if (str == null) {
    return false;
  }
  if (typeof str !== "string") {
    return false;
  }
  if (!str.match(/^\d/)) {
    return false;
  }
  if (str.length < 10) {
    return false;
  }
  return isValidDateValue(str);
};

export const getFolderUrn = (bim360Project: IBim360Project, nameOfFolder = "Project Files"): string => {
  const projectFolder = bim360Project.rootFolders.filter((folder) => folder.name === nameOfFolder)[0];
  return projectFolder.urn;
};

export const isFilterItemsValid = (filterItems: IFilterItem[]): boolean => {
  let isValid = true;
  if (isValueEmpty(filterItems)) {
    return false;
  }
  filterItems.forEach((item) => {
    if (isValueEmpty(item.label) || isValueEmpty(item.searchWord)) {
      isValid = false;
    }
  });
  return isValid;
};
/*
export const isAdmin = (state: IRootReducer) => {
  const { user } = state.globalReducer;
  if (user == null) {
    return false;
  }
  try {
    const member: IMember = find(state.adminReducer.project.members, (member) => member.user._id === user._id);
    return member && member.role === "ADMIN";
  } catch (err) {
    return false;
  }
};*/

export const isLoggedinUserMulticonsultUser = (state: IRootState) => {
  const { email } = state.globalReducer.user;
  return isEmailDomainMulticonsult(email);
};

export const getEmailDomain = (email: string) => {
  if (email == null) {
    return null;
  }
  const index = email.indexOf("@");
  const emailDomain = email.substr(index);
  return emailDomain.toUpperCase();
};

export const isEmailDomainMulticonsult = (email: string) => {
  const index = email.indexOf("@");
  const emailDomain = email.substr(index);
  return emailDomain.toUpperCase() === "@MULTICONSULT.NO";
};

export const isEmailDomainCowi = (email: string) => {
  const index = email.indexOf("@");
  const emailDomain = email.substr(index);
  return emailDomain.toUpperCase() === "@COWI.COM";
};

export const validateEmail = (email) => {
  var re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const emailShouldLoginWithOffice365 = (email: string) => {
  return isEmailDomainMulticonsult(email);
};

export const projectIsConnectedToBim360 = (state: IRootState) => {
  return state.adminReducer.project?.bim360.projectId != null;
};

export const projectIsConectedToArcGis = (state: IRootState) => {
  return state.adminReducer.project?.arcgis && state.adminReducer.project?.arcgis.groupId != null;
};

export const projectHasBotha2dAnd3dArcgisMap = (state: IRootState): boolean => {
  return (
    state.adminReducer.project?.arcgis?.default2dMap != null && state.adminReducer.project?.arcgis?.default3dMap != null
  );
};

// Converts a data URL to a File object
// Example usage: const file = dataURLToFile(dataUrl, "image.png");
export const dataURLToFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};
